"use client";

import React, { useState } from "react";
import Image from "next/image";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import { ZoomIn } from "~/components/icons";

import "yet-another-react-lightbox/styles.css";

import { buildDisplayImageUrl } from "~/lib/utils";

interface LightboxImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  className?: string;
  priority?: boolean;
  sizes?: string;
  objectFit?: "cover" | "contain";
  compact?: boolean;
}

export default function LightboxImage({
  src,
  alt,
  width,
  height,
  fill = false,
  className = "",
  priority = false,
  sizes,
  objectFit = "cover",
  compact = false,
}: LightboxImageProps) {
  const [open, setOpen] = useState(false);
  const displaySrc = buildDisplayImageUrl(src) ?? src;

  return (
    <>
      <div
        className={`relative cursor-pointer ${fill ? "h-full w-full" : ""}`}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        aria-label={`Open ${alt} in fullscreen mode`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setOpen(true);
          }
        }}
      >
        <Image
          src={displaySrc}
          alt={alt}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          fill={fill}
          className={`${
            objectFit === "contain"
              ? "object-contain object-center"
              : "object-cover object-top"
          } ${className}`}
          style={{ imageOrientation: "from-image" }}
          priority={priority}
          sizes={sizes}
        />
        {objectFit !== "contain" && !compact ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-interactive-3"></div>
        ) : null}
        <div
          className={`absolute rounded-full bg-interactive-2 ${
            compact ? "bottom-1 right-1 p-1" : "bottom-2 right-2 p-2"
          }`}
        >
          <ZoomIn
            className={`text-interactive-1 ${compact ? "h-3.5 w-3.5" : "h-6 w-6"}`}
          />
        </div>
      </div>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={[{ src: displaySrc }]}
        plugins={[Zoom]}
        carousel={{ finite: true }}
        animation={{ fade: 300 }}
        zoom={{
          maxZoomPixelRatio: 5,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
        }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
        controller={{
          closeOnBackdropClick: true,
          closeOnPullDown: true,
        }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, .9)" },
        }}
      />
    </>
  );
}
